import React, { useEffect, useMemo, useState } from 'react';
import Modal from '../Modal';
import SearchableDropdown from '../SearchableDropdown';
import type { ContractCustomProduct } from '../../utils/contractExtrasPdf';
import './ContractProductModal.css';

export type ContractProductSection = 'monthly' | 'accessory';

/** Just the catalog fields this dialog needs, so it works with either product shape. */
export interface ContractProductOption {
  id: string;
  name: string;
  type?: string;
  catalogStatus?: 'Active' | 'Void';
  replacementCostPerItem?: string;
}

interface ContractProductModalProps {
  isOpen: boolean;
  /** Which contract section the product is being added to. */
  section: ContractProductSection;
  inventoryProducts: ContractProductOption[];
  /** Product names already on the contract, so a duplicate can be called out. */
  existingNames: string[];
  onCancel: () => void;
  onAdd: (product: ContractCustomProduct) => void;
}

const INVENTORY_TYPES: Array<ContractCustomProduct['inventoryType']> = ['Robot', 'Accessory', 'Stock Item'];

/** Pull a number out of a catalog cost string like "$1,200" so it can prefill the price. */
function parseCostString(value?: string): string {
  const num = parseFloat(String(value ?? '').replace(/[^0-9.]/g, ''));
  return Number.isFinite(num) && num > 0 ? String(num) : '';
}

function newProductId(): string {
  return `cp-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 7)}`;
}

/**
 * Adds a product to a contract that is not one of the pre-printed catalog lines.
 * The name comes either from the inventory catalog or is written in on the spot;
 * either way the user sets the price here and fills the quantity back on the
 * contract form.
 */
const ContractProductModal: React.FC<ContractProductModalProps> = ({
  isOpen,
  section,
  inventoryProducts,
  existingNames,
  onCancel,
  onAdd,
}) => {
  const [step, setStep] = useState<'name' | 'price'>('name');
  const [source, setSource] = useState<'inventory' | 'custom'>('inventory');
  const [inventoryName, setInventoryName] = useState('');
  const [customName, setCustomName] = useState('');
  const [unitPrice, setUnitPrice] = useState('');
  const [addToInventory, setAddToInventory] = useState(true);
  const [inventoryType, setInventoryType] = useState<ContractCustomProduct['inventoryType']>(
    section === 'accessory' ? 'Accessory' : 'Robot'
  );
  const [error, setError] = useState('');

  // Reset every time the dialog opens so a previous entry never leaks in.
  useEffect(() => {
    if (!isOpen) return;
    setStep('name');
    setSource('inventory');
    setInventoryName('');
    setCustomName('');
    setUnitPrice('');
    setAddToInventory(true);
    setInventoryType(section === 'accessory' ? 'Accessory' : 'Robot');
    setError('');
  }, [isOpen, section]);

  // Voided catalog products are not offered.
  const activeProducts = useMemo(
    () => inventoryProducts.filter((p) => p.catalogStatus !== 'Void' && p.name),
    [inventoryProducts]
  );

  const productOptions = useMemo(
    () => Array.from(new Set(activeProducts.map((p) => p.name))).sort((a, b) => a.localeCompare(b)),
    [activeProducts]
  );

  const chosenName = (source === 'inventory' ? inventoryName : customName).trim();
  const selectedProduct = useMemo(
    () => activeProducts.find((p) => p.name === chosenName),
    [activeProducts, chosenName]
  );
  const isDuplicate = existingNames.some((name) => name.toLowerCase() === chosenName.toLowerCase());

  const sectionLabel =
    section === 'accessory' ? 'Additional Accessories' : 'Robot Quantity, Model and Monthly Cost per Item';
  const priceHint = section === 'accessory' ? 'One-time price per item' : 'Monthly price per item';

  const handleContinue = () => {
    if (!chosenName) {
      setError(source === 'inventory' ? 'Choose a product from inventory.' : 'Enter a name for the product.');
      return;
    }
    if (isDuplicate) {
      setError(`"${chosenName}" is already on this contract.`);
      return;
    }
    setError('');
    // Catalog products know their own replacement cost — offer it as the price.
    if (source === 'inventory' && !unitPrice) {
      setUnitPrice(parseCostString(selectedProduct?.replacementCostPerItem));
    }
    setStep('price');
  };

  const handleAdd = () => {
    const price = parseFloat(unitPrice.replace(/[$,]/g, ''));
    if (!Number.isFinite(price) || price < 0) {
      setError('Enter a price for this product.');
      return;
    }
    setError('');
    onAdd({
      id: newProductId(),
      name: chosenName,
      unitPrice: price,
      qty: '',
      section,
      inventoryProductId: source === 'inventory' ? selectedProduct?.id : undefined,
      addToInventory: source === 'custom' ? addToInventory : false,
      inventoryType: source === 'custom' && addToInventory ? inventoryType : undefined,
    });
  };

  return (
    <Modal isOpen={isOpen} onClose={onCancel} title="Add Product to Contract" elevated narrow>
      <div className="modal-body contract-product-modal">
        <p className="contract-product-modal-section">
          Adding to <strong>{sectionLabel}</strong>
        </p>

        {step === 'name' ? (
          <>
            <div className="contract-product-modal-source" role="group" aria-label="Product source">
              <label>
                <input
                  type="radio"
                  name="contract-product-source"
                  checked={source === 'inventory'}
                  onChange={() => {
                    setSource('inventory');
                    setCustomName('');
                    setError('');
                  }}
                />
                Inventory product
              </label>
              <label>
                <input
                  type="radio"
                  name="contract-product-source"
                  checked={source === 'custom'}
                  onChange={() => {
                    setSource('custom');
                    setInventoryName('');
                    setError('');
                  }}
                />
                Create a new product
              </label>
            </div>

            <div className="contract-form-group">
              <label className="form-label">Product name</label>
              {source === 'inventory' ? (
                <SearchableDropdown
                  options={productOptions}
                  value={inventoryName}
                  onChange={(value) => {
                    setInventoryName(value);
                    setError('');
                  }}
                  placeholder="Search inventory products..."
                  noResultsMessage="No product matches — switch to Create a new product to write it in"
                />
              ) : (
                <input
                  type="text"
                  className="form-input"
                  value={customName}
                  onChange={(e) => {
                    setCustomName(e.target.value);
                    setError('');
                  }}
                  placeholder="e.g. Kebb-i"
                  autoFocus
                />
              )}
            </div>
          </>
        ) : (
          <>
            <div className="contract-product-modal-chosen">
              <span className="contract-product-modal-chosen-name">{chosenName}</span>
              <button
                type="button"
                className="contract-product-modal-change"
                onClick={() => {
                  setStep('name');
                  setError('');
                }}
              >
                Change
              </button>
            </div>

            <div className="contract-form-group">
              <label className="form-label">{priceHint} ($)</label>
              <input
                type="number"
                min="0"
                step="0.01"
                className="form-input"
                value={unitPrice}
                onChange={(e) => {
                  setUnitPrice(e.target.value);
                  setError('');
                }}
                placeholder="0.00"
                autoFocus
              />
              <p className="contract-product-modal-hint">
                Prints on the agreement as “{chosenName} ($
                {unitPrice ? Number(unitPrice).toLocaleString() : '0'} each
                {section === 'accessory' ? ', one-time' : ''})”.
              </p>
            </div>

            {source === 'custom' && (
              <div className="contract-product-modal-catalog">
                <label className="contract-product-modal-checkbox">
                  <input
                    type="checkbox"
                    checked={addToInventory}
                    onChange={(e) => setAddToInventory(e.target.checked)}
                  />
                  Also add “{chosenName}” to the Inventory catalog
                </label>
                {addToInventory && (
                  <div className="contract-form-group">
                    <label className="form-label">Inventory product type</label>
                    <select
                      className="form-input"
                      value={inventoryType}
                      onChange={(e) =>
                        setInventoryType(e.target.value as ContractCustomProduct['inventoryType'])
                      }
                    >
                      {INVENTORY_TYPES.map((type) => (
                        <option key={type} value={type}>
                          {type}
                        </option>
                      ))}
                    </select>
                    <p className="contract-product-modal-hint">
                      The product is created when the contract is generated. An existing product with the
                      same name is reused instead of duplicated.
                    </p>
                  </div>
                )}
              </div>
            )}
          </>
        )}

        {error && <p className="contract-product-modal-error">{error}</p>}
      </div>

      <div className="modal-actions">
        {step === 'name' ? (
          <>
            <button type="button" className="cancel-button" onClick={onCancel}>
              Cancel
            </button>
            <button type="button" className="generate-link-button" onClick={handleContinue}>
              Next: Price
            </button>
          </>
        ) : (
          <>
            <button type="button" className="cancel-button" onClick={() => setStep('name')}>
              Back
            </button>
            <button type="button" className="generate-link-button" onClick={handleAdd}>
              Add to Contract
            </button>
          </>
        )}
      </div>
    </Modal>
  );
};

export default ContractProductModal;
